import { Rtt } from "@mui/icons-material";
import instance from "../auth/authApi";

export async function getPlace(placeId: string) {
  const response = await instance.get(`/place`, { params: { placeId } });
  return response.data;
}

/** Fetch place data by GPS coordinates (API reverse-geocodes to get place_id). */
export async function getPlaceByCoordinates(lat: number, lng: number) {
  const response = await instance.get(`/place`, { params: { lat, lng } });
  return response.data;
}

export async function getTrees(placeId: string, distance?: number) {
  const response = await instance.get(`/trees`, {
    params: { placeId, distance },
  });
  return response.data;
}

export async function getPlaceCopro(placeId: string) {
  const response = await instance.get(`/place-copro`, { params: { placeId } });
  return response.data;
}

/**
 * Page suivante du portefeuille syndic, ou autre filtre mandat.
 * La première page (mandat en_cours, limit 50) est déjà sur la fiche place-copro.
 * `mandat` vaut `en_cours` (fin de mandat aujourd'hui ou plus tard) ou `tous`.
 */
export async function getCoproPortfolio(params: {
  siret: string;
  page: number;
  limit: number;
  mandat: "en_cours" | "tous";
}) {
  const response = await instance.get(`/copro-portfolio`, { params });
  return response.data;
}

/** Toutes les copropriétés du SIREN, sans filtre de date. */
export async function exportCoproPortfolioCsv(siret: string) {
  const response = await instance.get(`/copro-portfolio/export`, {
    params: { siret },
    responseType: "blob",
  });
  return response;
}

export async function getPlaceSatelliteImage(placeId: string) {
  const response = await instance.get(`/place/satellite`, {
    params: { placeId },
  });
  return response.data;
}

export async function getPlaceStreetViewImages(placeId: string) {
  const response = await instance.get(`/place/street-views`, {
    params: { placeId },
  });
  return response.data;
}

export async function getHistoricMonuments(placeId: string) {
  const response = await instance.get(`/historic-monument`, {
    params: { placeId },
  });
  return response.data;
}

/** Sièges sociaux dont l'adresse correspond au lieu (placeId). */
export async function getBusinessAddressMatch(placeId: string) {
  const response = await instance.get(`/business-address`, {
    params: { placeId },
  });
  return response.data;
}

export async function getFireStations(placeId: string) {
  const response = await instance.get(`/fire-station`, {
    params: { placeId },
  });
  return response.data;
}

export async function getGasStations(placeId: string) {
  const response = await instance.get(`/gas-station`, {
    params: { placeId },
  });
  return response.data;
}

export async function getClimateRisk(placeId: string) {
  const response = await instance.get(`/risk`, { params: { placeId } });
  return response.data;
}

export async function getGeoJSONRisk({ placeId, typeRisk }: any) {
  const response = await instance.get(`/risk/${typeRisk}`, {
    params: { placeId, distance: 50000 }
  });
  return response.data;
}

export async function getBusinesses({
  placeId,
  nafCodeFilter,
  minDistance,
  maxDistance,
  resultPerPage,
  page,
}: any) {
  const response = await instance.get(`/businessV2`, {
    params: {
      placeId,
      nafCodeFilter,
      minDistance,
      maxDistance,
      resultPerPage,
      page,
    },
  });
  return response.data;
}

export async function getBusinessNafCodes() {
  const response = await instance.get(`/business/naf-codes`);
  return response.data;
}

export async function getWeatherAlert({
  date,
  postalCode,
}: {
  date: string;
  postalCode: string;
}) {
  const response = await instance.get(`/weather/alert`, {
    params: { date, postal_code: postalCode },
  });
  return response.data;
}

export async function getWeatherAlertRange() {
  const response = await instance.get(`/weather/alert/range`);
  return response.data;
}

export async function getUserAnnotationForPlaceId(placeId: any) {
  const response = await instance.get(`/annotation`, {
    params: { placeId },
  });
  return response.data;
}

export async function createOrUpdateUserAnnotationForPlaceId({
  placeId,
  annotation,
}: any) {
  const response = await instance.put(`/annotation`, annotation, {
    params: { placeId },
  });
  return response.data;
}

export async function getPerilForCoordinate({ lat, lng }: any) {
  const response = await instance.get(`/peril`, {
    params: { lat, lng },
  });
  return response.data;
}

export async function createPeril({ newPeril }: any) {
  const response = await instance.post(`/peril`, newPeril);
  return response.data;
}

export async function updatePerilById({ id, newPeril }: any) {
  const response = await instance.post(`/peril/${id}`, newPeril);
  return response.data;
}

export async function deletePerilById({ id }: any) {
  const reponse = await instance.delete(`/peril`, {
    params: { id },
  });
  return reponse.data;
}

export async function getAlertsForMap({
  isFeatureCollection,
}: {
  isFeatureCollection: boolean;
}) {
  const response = await instance.post(`/getAlert`, { isFeatureCollection });
  return response.data;
}

export async function getAlertsForMapDay({
  isFeatureCollection,
  startDate,
  endDate,
  yellow = undefined
}: {
  isFeatureCollection: boolean;
  startDate: Date;
  endDate: Date;
  yellow?: boolean
}) {
  const response = await instance.post(`/getAlert`, {
    isFeatureCollection,
    startDate,
    endDate,
    yellow
  });
  return response.data;
}

export async function createOrUpdateAlertForMap({ operation, alert }: any) {
  const response = await instance.post(`/alert/manage`, alert, {
    params: { operation },
  });
  return response.data;
}

export async function getUserInfo() {
  const response = await instance.get(`/userInfo`);
  const res = {
    name: response.data.name,
    groups: response.data.groups,
    preferred_username: response.data.preferred_username,
    given_name: response.data.given_name,
    family_name: response.data.family_name,
    email: response.data.email,
    rtp: response.data.rtp,
    tenants: Array.isArray(response.data.tenants) ? response.data.tenants : [],
  };
  return res;
}

export async function getListSiteSevesoByPlaceId({ placeId, distance }: any) {
  const response = await instance.get(`/risk/seveso`, {
    params: { placeId, distance },
  });
  return response.data;
}

export async function generatePdf(placeId: string, display?: string, tenantId?: string) {
  const response = await instance.get(`/pdf-generator`, {
    params: { placeId, display, ...(tenantId ? { tenantId } : {}) },
    responseType: "arraybuffer",
  });
  return { arraybuffer: response.data, status: response.status };
}

export async function uploadEventfile(formData: any) {
  const response = await instance.post(`/risk/event/upload`, formData, {headers: {'content-type': 'application/x-www-form-urlencoded'}});
  return response.data;
}

export async function getEvents({ resultPerPage, page }: { resultPerPage: number, page: number }) {
  const response = await instance.get(`/risk/event/all`, {params: { resultPerPage, page }});
  return response.data;
}

export async function uploadAddressfile(formData: any) {
  const response = await instance.post(`/address/bulk`, formData, {
    headers: {'content-type': 'application/x-www-form-urlencoded'},
    responseType: 'blob',
    timeout: 300000
  });
  return response.data;
}

export async function bulkAddressUsage() {
  const response = await instance.get(`/address/bulk/usage`);
  return response.data;
}

export async function getBulkProcessedFiles() {
  const response = await instance.get(`/address/bulk/files`);
  return response.data;
}

export async function getObservationsForDate( lat: number, lng: number, date: string ) {
  const response = await instance.get(`/weather/observation`, { params: {lat, lng, date }});
  return response.data;
}

export async function getWaterWays(placeId: string, distance?: number) {
  const response = await instance.get(`/water-way`, {
    params: { placeId, distance },
  });
  return response.data;
}

export async function getRailRoads(placeId: string, distance?: number) {
  const response = await instance.get(`/rail-road`, {
    params: { placeId, distance },
  });
  return response.data;
}

export async function getElevation(lat: number, lng: number) {
  const response = await instance.get(`/altitude`, {
    params: { lat, lng },
  });
  return response.data;
}

export async function getElevationFromGoogle(lat: number, lng: number) {
  const response = await instance.get(`/altitude/google`, {
    params: { lat, lng },
  });
  return response.data;
}

export async function getPerilsList({ page, limit, markedForDeletion = false }: { page: number, limit: number, markedForDeletion?: boolean }) {
  const response = await instance.get(`/peril/list`, {
    params: { page, limit, markedForDeletion }
  });
  return response.data;
}

export async function deletePerils(ids: string[]) {
  const response = await instance.delete(`/peril`, {
    data: { ids }
  });
  return response.data;
}

export async function getPerilAIResponses({ page, limit }: { page: number, limit: number }) {
  const response = await instance.get(`/peril-ai`, {
    params: { page, limit }
  });
  return response.data;
}